import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CognitoIdentityProvider, CognitoTokens } from "@repo/auth-core";
import type { AuthAdapter } from "./adapter";

interface AuthState {
  status: "loading" | "anonymous" | "authenticated" | "error";
  tokens: CognitoTokens | null;
  error: string | null;
}

interface AuthValue extends AuthState {
  login(
    returnTo?: string,
    identityProvider?: CognitoIdentityProvider,
  ): Promise<void>;
  logout(): void;
  invalidateSession(): void;
  /**
   * Returns a fresh ID token, awaiting the auth bootstrap if it's still in
   * flight. Throws if the bootstrap completes without a session.
   */
  getIdToken(): Promise<string>;
}

const AuthContext = createContext<AuthValue | null>(null);

const REFRESH_BUFFER_MS = 60_000;

/**
 * Platform-agnostic auth state machine. All Cognito/transport/storage
 * specifics are injected via {@link AuthAdapter}; this component owns the
 * status transitions, the single-flight token refresh, and the
 * `getIdToken()` contract the shared API layer depends on.
 */
export function AuthProvider({
  adapter,
  children,
}: {
  adapter: AuthAdapter;
  children: ReactNode;
}): ReactNode {
  // Tokens are always loaded asynchronously through the adapter (desktop
  // reads the OS keychain; web may complete a redirect exchange), so we
  // start in `loading` and let `bootstrap()` resolve the first real state.
  const [state, setState] = useState<AuthState>(() => ({
    status: "loading",
    tokens: null,
    error: null,
  }));

  // `getIdToken` is invoked from inside the typed openapi-fetch middleware
  // and may run during the bootstrap window before `state.tokens` lands. The
  // ref + the bootstrap promise together let it resolve correctly without
  // racing the React render cycle.
  const tokensRef = useRef<CognitoTokens | null>(null);
  const bootstrapDoneRef = useRef<Promise<void> | null>(null);
  const refreshInFlightRef = useRef<Promise<CognitoTokens> | null>(null);

  const commitTokens = useCallback(
    (tokens: CognitoTokens, status: AuthState["status"]) => {
      tokensRef.current = tokens;
      void adapter.persist(tokens);
      setState({ status, tokens, error: null });
    },
    [adapter],
  );

  const commitAnonymous = useCallback(
    (error: string | null = null) => {
      void adapter.clear();
      tokensRef.current = null;
      setState({
        status: error ? "error" : "anonymous",
        tokens: null,
        error,
      });
    },
    [adapter],
  );

  // First render: let the adapter complete any pending login callback and/or
  // read persisted tokens, then resolve to authenticated (refreshing if near
  // expiry) or anonymous.
  useEffect(() => {
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      let stored: CognitoTokens | null = null;
      try {
        stored = await adapter.bootstrap();
      } catch (err) {
        if (!cancelled) {
          commitAnonymous(err instanceof Error ? err.message : "Login failed");
        }
        return;
      }

      if (stored && stored.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
        if (!cancelled) commitTokens(stored, "authenticated");
        return;
      }
      if (stored) {
        try {
          const refreshed = await adapter.refresh(stored.refreshToken);
          if (!cancelled) commitTokens(refreshed, "authenticated");
          return;
        } catch (err) {
          if (cancelled) return;
          if (adapter.isRefreshErrorRetryable?.(err)) {
            // Keep the persisted session even when reopening the app offline.
            // getIdToken will retry refresh before sending any API request;
            // retaining these tokens does not send an expired credential.
            commitTokens(stored, "authenticated");
            return;
          }
        }
      }
      if (!cancelled) commitAnonymous();
    }

    // Fire exactly once for the lifetime of the provider — re-running would
    // re-enter the OAuth callback exchange.
    bootstrapDoneRef.current = bootstrap();
    return () => {
      cancelled = true;
    };
  }, [adapter, commitTokens, commitAnonymous]);

  const getIdToken = useCallback(async (): Promise<string> => {
    // First call(s) before bootstrap finishes: queue behind the same promise
    // so the captured `tokensRef.current` is the post-bootstrap value.
    if (bootstrapDoneRef.current) {
      await bootstrapDoneRef.current;
    }
    const current = tokensRef.current;
    if (!current) throw new Error("Not authenticated");
    if (current.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
      return current.idToken;
    }
    refreshInFlightRef.current ??= adapter.refresh(current.refreshToken);
    try {
      const refreshed = await refreshInFlightRef.current;
      tokensRef.current = refreshed;
      void adapter.persist(refreshed);
      setState((prev) => ({ ...prev, tokens: refreshed }));
      return refreshed.idToken;
    } catch (err) {
      // A temporary failure must leave the refresh token available for the
      // next request (including React Query retries after reconnecting).
      if (adapter.isRefreshErrorRetryable?.(err)) throw err;

      // Refresh token rejected (revoked/expired). The throw
      // happens before any request leaves the client, so the 401 path in the
      // API provider's `onUnauthorized` never fires. Wipe local state here so
      // the UI stops claiming we're authenticated and routes back to login
      // instead of looping failed queries against a dead token.
      await adapter.clear();
      tokensRef.current = null;
      setState({
        status: "error",
        tokens: null,
        error: "Session expired. Please sign in again.",
      });
      throw err;
    } finally {
      refreshInFlightRef.current = null;
    }
  }, [adapter]);

  const invalidateSession = useCallback((): void => {
    void adapter.clear();
    tokensRef.current = null;
    setState({
      status: "error",
      tokens: null,
      error: "Session expired. Please sign in again.",
    });
  }, [adapter]);

  const login = useCallback(
    async (
      returnTo: string = "/",
      identityProvider?: CognitoIdentityProvider,
    ): Promise<void> => {
      const tokens = await adapter.login(returnTo, identityProvider);
      // Web redirects away and resolves null (the callback completes on the
      // next load). Desktop resolves with tokens in-session.
      if (tokens) {
        commitTokens(tokens, "authenticated");
      }
    },
    [adapter, commitTokens],
  );

  const logout = useCallback((): void => {
    void adapter.clear();
    tokensRef.current = null;
    setState({ status: "anonymous", tokens: null, error: null });
    void adapter.logout();
  }, [adapter]);

  const value = useMemo<AuthValue>(
    () => ({
      ...state,
      login,
      logout,
      invalidateSession,
      getIdToken,
    }),
    [state, login, logout, invalidateSession, getIdToken],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
